import { keepPreviousData } from '@tanstack/react-query'
import { DEFAULT_WAGMI_REFETCH_POLICY, TANSTACK_REFETCH_POLICY_FAST } from 'config/RefetchPolicies'
import { useIslandById as useIslandByIdBackend, useIslandsQuery as useIslandsBackendQuery } from 'hooks/backend/islands'
import { useConnection } from 'hooks/useConnection'
import { debounce } from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { IslandApiResponse } from 'state/data/slice'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { useUpdateKodiakIslands } from 'state/kodiakIslands/hooks'
import { useSearchTerm } from 'state/kodiakIslands/hooks'
import { endDataTransition, setDataTransition } from 'state/kodiakIslands/reducer'

export interface IslandsQueryParams {
  orderBy?: string
  orderDirection?: string
  where?: Record<string, any>
}

export interface FilteredIslandsListParams {
  filter?: {
    onlyWithUserPosition?: boolean
    [key: string]: any
  }
  orderBy?: string
  orderDirection?: string
}

export interface IslandQueryResponse {
  isLoading: boolean
  isFetching: boolean
  isUninitialized: boolean
  isError: boolean
  data: IslandApiResponse[]
  refetch: () => void
}

/**
 * Transforms search terms to handle special cases like USDT → USD₮
 * @param term The search term to transform
 * @returns The transformed search term
 */
export const transformSearchTerm = (term: string): string => {
  if (!term) return term

  return term.replace(/usdt/i, 'USD₮')
}

export interface FilteredIslandsListResponse {
  data: IslandApiResponse[]
  filteredIslands: IslandApiResponse[]

  isLoading: boolean
  isFetching: boolean
  isUninitialized: boolean
  isError: boolean
  error?: any
  refetch: () => void

  smartFiltersUsed?: boolean

  isDataTransition: boolean
  transitionType: 'sort' | 'filter' | 'search' | null

  pagination: {
    limit: number
    offset: number
    goToPage: (page: number) => void
    currentPage: number
    totalPages: number
  }
}

/**
 * Hook to fetch islands data using the REST API
 * @param params Query parameters for the islands
 * @returns The islands data and query status
 */
export function useIslands(params: IslandsQueryParams = {}): IslandQueryResponse {
  const { chainId } = useConnection()
  const updateKodiakIslands = useUpdateKodiakIslands()

  const apiParams = useMemo(() => {
    const { orderBy, orderDirection } = params
    return {
      orderBy: orderBy === 'totalValueLockedUSD' ? 'totalTvl' : orderBy,
      orderDirection,
      chainId: chainId ?? undefined,
      limit: 100,
      offset: 0,
    }
  }, [params, chainId])

  const {
    data: apiIslands,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useIslandsBackendQuery(apiParams, {
    ...TANSTACK_REFETCH_POLICY_FAST,
    placeholderData: keepPreviousData,
  })

  const isUninitialized = !apiIslands && !isLoading

  useEffect(() => {
    if (apiIslands && apiIslands.count > 0) {
      updateKodiakIslands(apiIslands.data)
    }
  }, [apiIslands, updateKodiakIslands])

  return {
    isLoading,
    isFetching,
    isUninitialized,
    isError,
    data: apiIslands?.data || [],
    refetch,
  }
}

/**
 * Hook to fetch and filter islands with pagination, sorting, and polling
 * @param params Parameters for filtering islands
 * @returns The filtered islands data and pagination controls
 */

export function useFilteredIslandsList(params: FilteredIslandsListParams = {}): FilteredIslandsListResponse {
  const { orderBy: userOrderBy, orderDirection: userOrderDirection } = params
  const { chainId, account } = useConnection()
  const searchTerm = useSearchTerm()
  const filters = useAppSelector((state) => state.kodiakIslands.filters)
  const { dataTransition, transitionType } = useAppSelector((state) => state.kodiakIslands)
  const dispatch = useAppDispatch()

  const [pagination, setPagination] = useState({
    limit: 20,
    offset: 0,
    total: 0,
  })

  const isInitialRenderRef = useRef(true)
  const prevAccountRef = useRef(account)
  const prevSearchRef = useRef<string>('')
  const [isPaging, setIsPaging] = useState(false)

  const stateSort = useAppSelector((state) => state.kodiakIslands)
  const effectiveOrderBy = userOrderBy || stateSort.orderBy
  const effectiveOrderDirection = userOrderDirection || stateSort.orderDirection

  const lastSortRef = useRef({ orderBy: effectiveOrderBy, orderDirection: effectiveOrderDirection })
  const serializedFilters = useMemo(
    () => JSON.stringify(filters.map((f) => ({ name: f.displayName, active: f.isActive }))),
    [filters]
  )
  const lastFiltersRef = useRef(serializedFilters)

  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false
      return
    }

    const sortChanged =
      lastSortRef.current.orderBy !== effectiveOrderBy || lastSortRef.current.orderDirection !== effectiveOrderDirection

    if (sortChanged) {
      dispatch(setDataTransition({ inTransition: true, type: 'sort' }))
      if (pagination.offset > 0) {
        setPagination((prev) => ({ ...prev, offset: 0 }))
      }
    }

    lastSortRef.current = { orderBy: effectiveOrderBy, orderDirection: effectiveOrderDirection }
  }, [dispatch, pagination.offset, effectiveOrderBy, effectiveOrderDirection])

  useEffect(() => {
    if (isInitialRenderRef.current) {
      return
    }

    const filtersChanged = serializedFilters !== lastFiltersRef.current

    if (filtersChanged) {
      dispatch(setDataTransition({ inTransition: true, type: 'filter' }))
      if (pagination.offset > 0) {
        setPagination((prev) => ({ ...prev, offset: 0 }))
      }
    }

    lastFiltersRef.current = serializedFilters
  }, [serializedFilters, dispatch, pagination.offset])

  const debouncedSearch = useCallback(
    debounce((term: string) => {
      const trimmed = term?.trim() ?? ''
      const prev = prevSearchRef.current

      if (trimmed !== prev) {
        prevSearchRef.current = trimmed
        dispatch(setDataTransition({ inTransition: true, type: 'search' }))
        if (pagination.offset > 0) {
          setPagination((prev) => ({ ...prev, offset: 0 }))
        }
      }
    }, 500),
    [dispatch, pagination.offset]
  )

  useEffect(() => {
    if (!chainId) return
    setPagination((prev) => (prev.offset === 0 ? prev : { ...prev, offset: 0 }))
  }, [chainId])

  useEffect(() => {
    if (isInitialRenderRef.current) return
    debouncedSearch(searchTerm ?? '')
    return () => debouncedSearch.cancel()
  }, [searchTerm, debouncedSearch])

  const filterParams = useMemo(() => {
    const volatileFilter = filters.find((f) => f.displayName === 'Volatile')
    const stableFilter = filters.find((f) => f.displayName === 'Stable')

    return {
      incentivized: filters.find((f) => f.displayName === 'Incentivized')?.isActive,
      volatile: volatileFilter?.isActive === true && stableFilter?.isActive !== true,
      stable: stableFilter?.isActive === true && volatileFilter?.isActive !== true,
      hideLowTvl: filters.find((f) => f.displayName === 'Hide Low TVL')?.isActive,
      manager: filters.find((f) => f.displayName === 'Auto-PoL')?.isActive,
      withBaults: filters.find((f) => f.displayName === 'Baults')?.isActive,
      managed: filters.find((f) => f.displayName === 'Managed')?.isActive,
      permissionless: filters.find((f) => f.displayName === 'Permissionless')?.isActive,
    }
  }, [filters])

  const apiParams = useMemo(() => {
    const params: Record<string, any> = {
      limit: pagination.limit,
      offset: pagination.offset,
      chainId: chainId ?? undefined,
      orderBy: effectiveOrderBy,
      orderDirection: effectiveOrderDirection,
    }

    if (account && account.trim()) {
      params.user = account
    }

    if (filterParams.incentivized) {
      params.rewardVault = true
      params.sweetened = true
    }

    if (filterParams.volatile === true) {
      params.volatile = true
    } else if (filterParams.stable === true) {
      params.volatile = false
    }

    if (filterParams.hideLowTvl) params.minimumTvl = 10000
    if (filterParams.manager) params.managerTreasury = '0xaCb87Ee21aBF6718261e2a3A520F8239103b3Fa8'.toLowerCase()
    if (filterParams.withBaults !== undefined) params.withBaults = filterParams.withBaults

    if (filterParams.managed) {
      params.managed = true
    } else if (filterParams.permissionless) {
      params.managed = false
    }

    if (searchTerm && searchTerm.trim() !== '') {
      params.search = transformSearchTerm(searchTerm)
      params.smartFilters = true
    }

    return params
  }, [
    account,
    pagination.limit,
    pagination.offset,
    chainId,
    effectiveOrderBy,
    effectiveOrderDirection,
    filterParams.incentivized,
    filterParams.volatile,
    filterParams.stable,
    filterParams.hideLowTvl,
    filterParams.manager,
    filterParams.withBaults,
    filterParams.managed,
    filterParams.permissionless,
    searchTerm,
  ])

  function hasAtLeastOneValidQueryParam(params: Record<string, any>): boolean {
    const relevantKeys = [
      'orderBy',
      'orderDirection',
      'search',
      'chainId',
      'rewardVault',
      'sweetened',
      'volatile',
      'minimumTvl',
      'user',
      'managerTreasury',
      'withBaults',
      'smartFilters',
      'managed',
    ]

    return relevantKeys.some((key) => {
      const value = params[key]
      return value !== undefined && value !== ''
    })
  }

  const shouldSkipQuery = !chainId || !hasAtLeastOneValidQueryParam(apiParams)

  const {
    data: apiData,
    isLoading,
    isFetching,
    isError,
    error,
    isSuccess,
    refetch,
  } = useIslandsBackendQuery(apiParams, {
    enabled: !shouldSkipQuery,
    ...TANSTACK_REFETCH_POLICY_FAST,
    placeholderData: keepPreviousData,
  })

  const data = useMemo(() => apiData?.data || [], [apiData?.data])
  const count = apiData?.count ?? 0
  const smartFiltersUsed = apiData?.smartFiltersUsed
  const isUninitialized = shouldSkipQuery && !apiData && !isLoading

  const isCacheHit = isSuccess && !isLoading && !isFetching && !isUninitialized

  useEffect(() => {
    if (prevAccountRef.current !== account) {
      prevAccountRef.current = account
      if (dataTransition) {
        dispatch(endDataTransition())
      }
      if (!shouldSkipQuery) {
        refetch()
      }
    }
  }, [account, dataTransition, refetch, shouldSkipQuery, dispatch])

  useEffect(() => {
    if (isSuccess) {
      setPagination((prev) => ({ ...prev, total: count }))

      if (isPaging) {
        setIsPaging(false)
      }

      if (dataTransition) {
        if (isCacheHit) {
          dispatch(endDataTransition())
        } else if (!isFetching) {
          dispatch(endDataTransition())
        }
      }
    }

    if (isError && dataTransition) {
      dispatch(endDataTransition())
    }
  }, [isSuccess, count, isPaging, dataTransition, isCacheHit, isFetching, isError, dispatch])

  useEffect(() => {
    if (dataTransition && shouldSkipQuery) {
      dispatch(endDataTransition())
    }
  }, [dataTransition, shouldSkipQuery, dispatch])

  useEffect(() => {
    if (dataTransition) {
      const transitionTimeout = setTimeout(() => {
        dispatch(endDataTransition())
      }, 3000)

      return () => clearTimeout(transitionTimeout)
    }
  }, [dataTransition, dispatch])

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(pagination.total / pagination.limit)),
    [pagination.total, pagination.limit]
  )

  const currentPage = useMemo(
    () => Math.floor(pagination.offset / pagination.limit),
    [pagination.offset, pagination.limit]
  )

  const goToPage = useCallback((page: number) => {
    setIsPaging(true)
    setPagination((prev) => ({
      ...prev,
      offset: page * prev.limit,
    }))
  }, [])

  const dataToDisplay = useMemo(() => {
    if (dataTransition && !isPaging) {
      return []
    }
    return data
  }, [data, dataTransition, isPaging])

  const isLoadingState = useMemo(() => {
    return (isLoading && !isError) || (dataTransition && !isPaging)
  }, [isLoading, isError, dataTransition, isPaging])

  return {
    isLoading: isLoadingState,
    isFetching,
    isUninitialized,
    isError,
    data: dataToDisplay,
    refetch,
    smartFiltersUsed,
    filteredIslands: dataToDisplay,
    isDataTransition: dataTransition,
    transitionType,
    error,
    pagination: {
      limit: pagination.limit,
      offset: pagination.offset,
      goToPage,
      currentPage,
      totalPages,
    },
  }
}

/**
 * Hook to fetch a single island by ID using the REST API
 * @param id The ID of the island to fetch
 * @returns The island data and query status
 */
export function useIslandById(id: string | null | undefined) {
  const [suppress, setSuppress] = useState(false)

  const normalizedId = useMemo(() => {
    if (!id || suppress) return ''
    return id.toLowerCase()
  }, [id, suppress])

  const { data, isLoading, isFetching, isError, error, refetch } = useIslandByIdBackend(normalizedId, {
    skip: !normalizedId,
    ...(!suppress ? DEFAULT_WAGMI_REFETCH_POLICY : { refetchInterval: false }),
    refetchOnWindowFocus: !suppress,
    refetchOnReconnect: !suppress,
  })

  useEffect(() => {
    const status = (error as any)?.status ?? (error as any)?.originalStatus
    if (isError && status === 404) {
      setSuppress(true)
    }
  }, [isError, error])

  const retry = () => {
    setSuppress(false)
    refetch()
  }

  return {
    isLoading: !data && isLoading,
    isFetching,
    isUninitialized: !normalizedId,
    isError,
    error,
    data: data ?? null,
    refetch: retry,
  }
}
